import React from 'react';
import { DATA_MANAGEMENT_PAGE_SIZE_OPTIONS } from '../../../utils/dataManagementListUtils';

export type DataManagementPaginationProps = {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalCount?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

function buildPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result: (number | 'ellipsis')[] = [];

  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) {
      result.push('ellipsis');
    }
    result.push(page);
  });

  return result;
}

const DataManagementPagination: React.FC<DataManagementPaginationProps> = ({
  pageNumber,
  pageSize,
  totalPages,
  totalCount,
  onPageChange,
  onPageSizeChange,
}) => {
  const safeTotalPages = Math.max(1, totalPages);
  const pageNumbers = buildPageNumbers(pageNumber, safeTotalPages);

  return (
    <div className="sb-data-management-pagination d-flex flex-wrap justify-content-between align-items-center gap-2 p-3">
      <div className="d-flex align-items-center gap-2">
        <label className="form-label form-label-sm mb-0 text-nowrap text-body-secondary" htmlFor="dm-page-size">
          Sayfa boyutu
        </label>
        <select
          id="dm-page-size"
          className="form-select form-select-sm w-auto"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {DATA_MANAGEMENT_PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        {totalCount != null && (
          <span className="sb-data-management-meta small text-nowrap">{totalCount} kayıt</span>
        )}
      </div>

      <nav aria-label="Sayfalama">
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${pageNumber <= 1 ? 'disabled' : ''}`}>
            <button
              type="button"
              className="page-link"
              disabled={pageNumber <= 1}
              onClick={() => onPageChange(pageNumber - 1)}
              aria-label="Önceki sayfa"
            >
              ‹
            </button>
          </li>
          {pageNumbers.map((page, index) =>
            page === 'ellipsis' ? (
              <li key={`ellipsis-${index}`} className="page-item disabled">
                <span className="page-link">…</span>
              </li>
            ) : (
              <li key={page} className={`page-item ${page === pageNumber ? 'active' : ''}`}>
                <button type="button" className="page-link" onClick={() => onPageChange(page)}>
                  {page}
                </button>
              </li>
            )
          )}
          <li className={`page-item ${pageNumber >= safeTotalPages ? 'disabled' : ''}`}>
            <button
              type="button"
              className="page-link"
              disabled={pageNumber >= safeTotalPages}
              onClick={() => onPageChange(pageNumber + 1)}
              aria-label="Sonraki sayfa"
            >
              ›
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default DataManagementPagination;
